import { createContext, useContext } from "react";
import type { UsersResponse } from "@/lib/pb";

/**
 * `connecting`  — the first reachability check has not finished yet.
 * `unreachable` — the Kobo server did not answer; we cannot know the state.
 * `signed-out`  — the server answered and there is no valid session.
 * `signed-in`   — there is a valid session for the signed-in user.
 */
export type SessionStatus = "connecting" | "unreachable" | "signed-out" | "signed-in";

export type Session = {
  status: SessionStatus;
  user: UsersResponse | null;
  /** Populated while `status` is `unreachable`. */
  connectionError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Re-run the reachability check, e.g. from a "Try again" button. */
  reconnect: () => Promise<void>;
};

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return session;
}

/** The signed-in user, or null. Convenience wrapper over `useSession`. */
export function useCurrentUser(): UsersResponse | null {
  return useSession().user;
}
